import type {
  CanvasDatabasePresentationState,
  CanvasDatabasePresentationViewport,
  CanvasDatabasePresentationViewportTarget,
  DatabaseBlockModel,
  ViewBasicDataType,
} from '@blocksuite/affine-model';
import type { DatabaseFlags } from '@blocksuite/data-view';

import type { DatabaseBlockDataSource } from './data-source.js';

type CanvasPresentationDataSource = Pick<
  DatabaseBlockDataSource,
  'storageModel' | 'canvasPresentationSet'
>;

export const CANVAS_DATABASE_VIEWPORT_QUANTUM = 16;

export type CanvasPresentationResolution =
  | {
      kind: 'summary';
      state: CanvasDatabasePresentationState;
    }
  | {
      kind: 'live';
      state: CanvasDatabasePresentationState;
      view: ViewBasicDataType;
    }
  | {
      kind: 'invalid';
      state: CanvasDatabasePresentationState;
      reason: string;
    };

const DEFAULT_VIEWPORT: CanvasDatabasePresentationViewport = {
  scrollLeft: 0,
  scrollTop: 0,
};

function createDefaultViewport(): CanvasDatabasePresentationViewport {
  return {
    ...DEFAULT_VIEWPORT,
  };
}

export function getCanvasPresentationState(
  model: DatabaseBlockModel,
  views: readonly ViewBasicDataType[] = model.props.views
): CanvasDatabasePresentationState {
  if (model.props.canvasPresentation) {
    return model.props.canvasPresentation;
  }

  const firstView = views[0];
  if (!firstView) {
    return { mode: 'summary' };
  }

  return {
    mode: 'live',
    viewId: firstView.id,
    viewport: createDefaultViewport(),
  };
}

export function resolveCanvasPresentation(
  model: DatabaseBlockModel,
  views: readonly ViewBasicDataType[] = model.props.views,
  state: CanvasDatabasePresentationState = getCanvasPresentationState(
    model,
    views
  )
): CanvasPresentationResolution {
  if (state.mode === 'summary') {
    return {
      kind: 'summary',
      state,
    };
  }

  if (!state.viewId) {
    return {
      kind: 'invalid',
      reason: 'Canvas presentation view is missing.',
      state,
    };
  }
  if (
    !(
      state as {
        viewport?: CanvasDatabasePresentationViewport;
      }
    ).viewport
  ) {
    return {
      kind: 'invalid',
      reason: 'Canvas presentation viewport is missing.',
      state,
    };
  }

  const view = views.find(view => view.id === state.viewId);
  if (!view) {
    return {
      kind: 'invalid',
      reason: 'Canvas presentation view no longer exists.',
      state,
    };
  }

  return {
    kind: 'live',
    state,
    view,
  };
}

export function createCanvasLivePresentationState(
  viewId: string,
  now = Date.now()
): CanvasDatabasePresentationState {
  return {
    mode: 'live',
    viewId,
    viewport: createDefaultViewport(),
    updatedAt: now,
  };
}

export function createCanvasSummaryPresentationState(
  now = Date.now()
): CanvasDatabasePresentationState {
  return {
    mode: 'summary',
    updatedAt: now,
  };
}

export function getFallbackCanvasPresentationViewAfterDelete(
  views: readonly ViewBasicDataType[],
  deletedViewId: string
): ViewBasicDataType | undefined {
  const deletedIndex = views.findIndex(view => view.id === deletedViewId);
  if (deletedIndex < 0) {
    return;
  }
  return (
    views[deletedIndex + 1] ??
    views[deletedIndex - 1] ??
    views.find(view => view.id !== deletedViewId)
  );
}

export function getCanvasPresentationStateAfterViewDelete(
  model: DatabaseBlockModel,
  deletedViewId: string,
  views: readonly ViewBasicDataType[] = model.props.views,
  now = Date.now()
): CanvasDatabasePresentationState | undefined {
  const state = model.props.canvasPresentation;
  if (
    state?.mode !== 'live' ||
    !state.viewId ||
    state.viewId !== deletedViewId
  ) {
    return;
  }

  const fallbackView = getFallbackCanvasPresentationViewAfterDelete(
    views,
    deletedViewId
  );
  return fallbackView
    ? createCanvasLivePresentationState(fallbackView.id, now)
    : createCanvasSummaryPresentationState(now);
}

export function getRepairedCanvasPresentationState(
  model: DatabaseBlockModel,
  views: readonly ViewBasicDataType[] = model.props.views,
  state: CanvasDatabasePresentationState = getCanvasPresentationState(
    model,
    views
  ),
  now = Date.now()
): CanvasDatabasePresentationState | undefined {
  if (state.mode !== 'live' || !state.viewId) {
    return;
  }
  if (
    !(
      state as {
        viewport?: CanvasDatabasePresentationViewport;
      }
    ).viewport
  ) {
    return;
  }
  if (views.some(view => view.id === state.viewId)) {
    return;
  }

  const fallbackView = views[0];
  return fallbackView
    ? createCanvasLivePresentationState(fallbackView.id, now)
    : createCanvasSummaryPresentationState(now);
}

export function createCanvasPresentationFeatureFlags(
  baseFlags: DatabaseFlags
): DatabaseFlags {
  return {
    ...baseFlags,
    enable_table_virtual_scroll: true,
    use_shared_canvas_viewport: true,
  };
}

export function shouldApplyObservedCanvasViewport(
  hasPendingLocalScrollCommit: boolean
): boolean {
  return !hasPendingLocalScrollCommit;
}

export function formatCanvasPresentationCellValue(value: unknown): string {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value
      .map(item => formatCanvasPresentationCellValue(item))
      .filter(Boolean)
      .join(', ');
  }
  if (typeof value === 'object') {
    const objectValue = value as Record<string, unknown>;
    for (const key of ['name', 'title', 'text']) {
      const text = formatCanvasPresentationCellValue(objectValue[key]);
      if (text) {
        return text;
      }
    }
    const stringify = (value as { toString?: () => string }).toString;
    if (stringify && stringify !== Object.prototype.toString) {
      try {
        const text = stringify.call(value).trim();
        if (text && text !== '[object Object]') {
          return text;
        }
      } catch {
        return '';
      }
    }
    return '';
  }
  return '';
}

export function quantizeCanvasViewport(
  viewport: CanvasDatabasePresentationViewport
): CanvasDatabasePresentationViewport {
  const next: CanvasDatabasePresentationViewport = {
    ...quantizeCanvasViewportTarget(viewport),
  };
  if (viewport.target) {
    next.target = viewport.target;
  }
  if (viewport.targets) {
    next.targets = Object.fromEntries(
      Object.entries(viewport.targets).map(([target, position]) => [
        target,
        quantizeCanvasViewportTarget(position),
      ])
    );
  }
  return next;
}

export function updateCanvasPresentationState(
  dataSource: CanvasPresentationDataSource,
  state: CanvasDatabasePresentationState
): void {
  dataSource.canvasPresentationSet(state);
}

export function updateCanvasPresentationViewport(
  dataSource: CanvasPresentationDataSource,
  viewport: CanvasDatabasePresentationViewport,
  now = Date.now(),
  views: readonly ViewBasicDataType[] = dataSource.storageModel.props.views
): void {
  const state = getCanvasPresentationState(dataSource.storageModel, views);
  if (state.mode !== 'live') {
    throw new Error('Cannot update viewport without an active canvas view.');
  }
  updateCanvasPresentationState(dataSource, {
    ...state,
    viewport: quantizeCanvasViewport(viewport),
    updatedAt: now,
  });
}

function quantizeNonNegative(value: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    return 0;
  }
  return (
    Math.round(value / CANVAS_DATABASE_VIEWPORT_QUANTUM) *
    CANVAS_DATABASE_VIEWPORT_QUANTUM
  );
}

function quantizeCanvasViewportTarget(
  viewport: CanvasDatabasePresentationViewportTarget
): CanvasDatabasePresentationViewportTarget {
  const next: CanvasDatabasePresentationViewportTarget = {
    scrollLeft: quantizeNonNegative(viewport.scrollLeft),
    scrollTop: quantizeNonNegative(viewport.scrollTop),
  };
  if (viewport.rowAnchor) {
    next.rowAnchor = {
      offsetPx: quantizeNonNegative(viewport.rowAnchor.offsetPx),
      offsetRatio: Number.isFinite(viewport.rowAnchor.offsetRatio)
        ? Math.round(viewport.rowAnchor.offsetRatio * 1000) / 1000
        : 0,
      rowId: viewport.rowAnchor.rowId,
    };
  }
  return next;
}

/** Show `viewId` live on the canvas; a no-op when it is already shown. */
export function presentCanvasDatabaseView(
  dataSource: CanvasPresentationDataSource,
  viewId: string
): void {
  const model = dataSource.storageModel;
  const views = model.props.views;
  if (!views.some(view => view.id === viewId)) {
    throw new Error(`Database view "${viewId}" does not exist.`);
  }
  const state = getCanvasPresentationState(model, views);
  if (state.mode === 'live' && state.viewId === viewId) {
    return;
  }
  updateCanvasPresentationState(
    dataSource,
    createCanvasLivePresentationState(viewId)
  );
}
